import { useState } from 'react'
import { useTranslate } from 'shared/lib/i18n'
import type { RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { Button } from 'shared/components/ui/button'
import { ScanInput } from 'shared/components/scan-input'
import { Hint } from 'shared/components/section'
import { ToneBanner } from 'shared/components/tone-banner'

/**
 * The scan bar on the parcel search screen (ticket 09). An operator scans a code
 * — by USB wedge, phone camera or by typing — and this asks the server's one
 * total resolver (ADR-0001) what it is. A `resolved` scan opens that Parcel's
 * detail; an `ambiguous` scan (the same bare code under more than one partner)
 * offers the candidates to pick from; an `unresolved` scan says so plainly.
 *
 * A code no Parcel carries may be a Container's — our Bag (`M-…`) or a partner's Box
 * (ticket 48, user story 126): one found opens its card; one code two partners print
 * offers both to pick from. Any other code — a client code, part of a code — the
 * page searches by (`onUnresolved`): the scan field is the search's code field too.
 *
 * What opened nothing is answered as every scanning page answers (`ToneBanner`):
 * «отказ» for an unknown code, «внимание» with the choices for a shared one.
 *
 * Resolution is the server's; this only routes the outcome. The resolver call is
 * injected (`resolve`) so it is the stubbed tRPC seam in tests (spec Testing
 * Decisions — the UI seam is the tRPC client, stubbed).
 */

type ScanResolution = RouterOutputs['parcels']['ResolveScan']
type Candidate = Extract<ScanResolution, { outcome: 'ambiguous' }>['candidates'][number]
type ContainerHit = RouterOutputs['parcels']['FindContainers'][number]

/** The answer to the last scan that opened nothing by itself. */
type ScanAnswer =
	| { kind: 'not_found'; code: string }
	| { kind: 'draining'; code: string }
	| { kind: 'ambiguous'; code: string; candidates: Candidate[] }
	| { kind: 'containers'; code: string; containers: ContainerHit[] }

function findContainersViaTrpc(code: string): Promise<ContainerHit[]> {
	return trpcClient.parcels.FindContainers.query({ code })
}

function resolveViaTrpc(locationId?: string) {
	return (code: string): Promise<ScanResolution> =>
		trpcClient.parcels.ResolveScan.query(
			locationId ? { code, pointId: locationId } : { code },
		)
}

export interface ScanBarProps {
	onOpen: (parcelId: string) => void
	/**
	 * The operator's current Point, when one is selected. On a DRAINING Point a
	 * code that resolves to nothing is answered with the "still draining, look in
	 * the legacy panel" hint instead of a bare not-found (spec §11).
	 */
	locationId?: string
	/** Test seam: the resolver call. Defaults to the real tRPC client. */
	resolve?: (code: string) => Promise<ScanResolution>
	/** Opens a Container's card; without it a Container's code is not looked for. */
	onOpenContainer?: (containerId: string) => void
	/** Test seam: the Containers a code names. Defaults to the real tRPC client. */
	findContainers?: (code: string) => Promise<ContainerHit[]>
	/**
	 * A code that names no one Parcel and no Container — a client code, part of a code:
	 * the page searches by it instead of saying «не найдена».
	 */
	onUnresolved?: (code: string) => void
	/** What the field takes, behind an «i» beside it. */
	hint?: string
}

export function ScanBar({ onOpen, locationId, resolve, onOpenContainer, findContainers, onUnresolved, hint }: ScanBarProps) {
	const { t } = useTranslate()
	const [answer, setAnswer] = useState<ScanAnswer | null>(null)
	const [resolving, setResolving] = useState(false)
	// A new round remounts the field, so the cursor is back in it after every scan.
	const [round, setRound] = useState(0)

	const doResolve = resolve ?? resolveViaTrpc(locationId)

	const handleScan = async (code: string) => {
		setResolving(true)
		setAnswer(null)
		try {
			const result = await doResolve(code)
			if (result.outcome === 'resolved') {
				onOpen(result.parcelId)
			} else if (result.outcome === 'ambiguous') {
				setAnswer({ kind: 'ambiguous', code, candidates: result.candidates })
			} else if (result.outcome === 'draining') {
				setAnswer({ kind: 'draining', code })
			} else {
				const hits = onOpenContainer ? await (findContainers ?? findContainersViaTrpc)(code) : []
				if (hits.length === 1) onOpenContainer!(hits[0]!.containerId)
				else if (hits.length > 1) setAnswer({ kind: 'containers', code, containers: hits })
				else if (onUnresolved) onUnresolved(code)
				else setAnswer({ kind: 'not_found', code })
			}
		} finally {
			setResolving(false)
			setRound((r) => r + 1)
		}
	}

	return (
		<section className='flex flex-col gap-3'>
			<div className='flex items-center gap-2'>
				<div className='flex-1'>
					<ScanInput key={round} onScan={handleScan} disabled={resolving} />
				</div>
				{hint && <Hint>{hint}</Hint>}
			</div>
			{answer?.kind === 'not_found' && (
				<ToneBanner tone='refused' title={t('parcels.scanNotFound')} detail={answer.code} testId='scan-not-found' />
			)}
			{answer?.kind === 'draining' && (
				<ToneBanner
					tone='attention'
					title={t('parcels.scanDrainingTitle')}
					detail={`${answer.code} · ${t('parcels.scanDraining')}`}
					testId='scan-draining'
				/>
			)}
			{answer?.kind === 'containers' && (
				<ToneBanner
					tone='attention'
					title={t('parcels.scanContainersTitle')}
					detail={`${answer.code} · ${t('lookup.scanContainerChoose')}`}
					testId='scan-containers'
				>
					<div className='flex flex-wrap gap-2 pt-1'>
						{answer.containers.map((c) => (
							<Button key={c.containerId} variant='outline' onClick={() => onOpenContainer?.(c.containerId)}>
								{t(`containerKind.${c.kind}`)} {c.code}
								{c.partner ? ` · ${c.partner.name}` : ''}
							</Button>
						))}
					</div>
				</ToneBanner>
			)}
			{answer?.kind === 'ambiguous' && (
				<ToneBanner
					tone='attention'
					title={t('parcels.scanAmbiguousTitle')}
					detail={`${answer.code} · ${t('parcels.scanAmbiguous')}`}
					testId='scan-ambiguous'
				>
					<div className='flex flex-wrap gap-2 pt-1'>
						{answer.candidates.map((candidate) => (
							<Button key={candidate.parcelId} variant='outline' onClick={() => onOpen(candidate.parcelId)}>
								{candidate.trackingCode}
							</Button>
						))}
					</div>
				</ToneBanner>
			)}
		</section>
	)
}
